import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import valueChainPermissionsAPI from '../api/valueChainPermissions';

// Types
interface ValueChainAccess {
  id: number;
  value_chain_id: string;
  value_chain_name: string;
  permission_level: string;
  is_active: boolean;
  granted_at: string;
  expires_at?: string;
  granted_by?: number;
  notes?: string;
}

interface ValueChain {
  element_id: string;
  value_chain_id: string; // For backward compatibility
  name: string;
  value_chain_name: string; // For backward compatibility
  permission_level: string;
  granted_at: string;
  expires_at?: string;
}

interface ValueChainWithPermission {
  element_id: string;
  name: string;
  has_access: boolean;
  permission_level?: string;
  is_active?: boolean;
  granted_at?: string;
  expires_at?: string;
}

interface GrantAccessRequest {
  value_chain_id: string;
  value_chain_name: string;
  permission_level?: string;
  notes?: string;
}

interface UseValueChainPermissionsReturn {
  accessibleValueChains: ValueChain[];
  allValueChains: ValueChainWithPermission[];
  loading: boolean;
  error: string | null;
  loadAccessibleValueChains: () => Promise<void>;
  loadAllValueChains: () => Promise<void>;
  grantAccess: (request: GrantAccessRequest) => Promise<{ success: boolean; error?: string }>;
  revokeAccess: (permissionId: number) => Promise<{ success: boolean; error?: string }>;
  hasAccess: (valueChainId: string) => boolean;
  getPermissionLevel: (valueChainId: string) => string | null;
  refreshPermissions: () => Promise<void>;
  hasAnyValueChainAccess: (level?: string) => boolean;
  getAccessibleValueChains: (level?: string) => ValueChain[];
  canViewValueChain: (valueChainId: string) => boolean;
}

/**
 * Hook for managing value chain permissions
 * 
 * This hook provides functionality to:
 * - Get accessible value chains for the current user
 * - Check if user has access to specific value chains
 * - Request access to new value chains
 * - Revoke access to value chains
 */
export const useValueChainPermissions = (): UseValueChainPermissionsReturn => {
  const { isAuthenticated, user, initialized, permissions } = useAuth();
  const [accessibleValueChains, setAccessibleValueChains] = useState<ValueChain[]>([]);
  const [allValueChains, setAllValueChains] = useState<ValueChainWithPermission[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load accessible value chains for the current user
   */
  const loadAccessibleValueChains = useCallback(async (): Promise<void> => {
    if (!initialized || !isAuthenticated || !user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('=== LOADING ACCESSIBLE VALUE CHAINS ===');
      console.log('Using permissions from AuthProvider...');
      console.log('Permissions object:', permissions);
      console.log('Value chains from permissions:', permissions?.valueChains);
      
      // Use permissions from AuthProvider instead of calling API
      if (permissions?.valueChains && permissions.valueChains.length > 0) {
        console.log('Loading accessible value chains from permissions:', permissions.valueChains);
        
        // Convert permission data to value chain format
        const accessibleChains: ValueChain[] = permissions.valueChains
          .filter((access: ValueChainAccess) => access.is_active)
          .map((access: ValueChainAccess) => ({
            element_id: access.value_chain_id,
            value_chain_id: access.value_chain_id, // For backward compatibility
            name: access.value_chain_name,
            value_chain_name: access.value_chain_name, // For backward compatibility
            permission_level: access.permission_level,
            granted_at: access.granted_at,
            expires_at: access.expires_at
          }));

        console.log('Converted accessible value chains:', accessibleChains);
        setAccessibleValueChains(accessibleChains);
      } else {
        console.log('No value chain permissions found in AuthProvider');
        setAccessibleValueChains([]);
      }
    } catch (err: any) {
      console.error('Error loading accessible value chains:', err);
      setError('Failed to load accessible value chains');
    } finally {
      setLoading(false);
    }
  }, [initialized, isAuthenticated, user, permissions]);

  /**
   * Load all value chains with permission status
   */
  const loadAllValueChains = useCallback(async (): Promise<void> => {
    if (!initialized || !isAuthenticated || !user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await valueChainPermissionsAPI.getAllValueChainsWithPermissions();
      
      if (response.success && response.data) {
        setAllValueChains(response.data);
      } else {
        setError(response.error || 'Failed to load value chains');
      }
    } catch (err: any) {
      console.error('Error loading all value chains:', err);
      setError('Failed to load value chains');
    } finally {
      setLoading(false);
    }
  }, [initialized, isAuthenticated, user]);

  /**
   * Grant access to a value chain
   */
  const grantAccess = useCallback(async (request: GrantAccessRequest): Promise<{ success: boolean; error?: string }> => {
    if (!isAuthenticated || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const response = await valueChainPermissionsAPI.grantAccess(request);
      
      if (response.success) {
        // Refresh permissions after successful grant
        await loadAccessibleValueChains();
        return { success: true };
      } else {
        return { success: false, error: response.error || 'Failed to grant access' };
      }
    } catch (err: any) {
      console.error('Error granting access:', err);
      return { success: false, error: 'Failed to grant access' };
    }
  }, [isAuthenticated, user, loadAccessibleValueChains]);

  /**
   * Revoke access to a value chain
   */
  const revokeAccess = useCallback(async (permissionId: number): Promise<{ success: boolean; error?: string }> => {
    if (!isAuthenticated || !user) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const response = await valueChainPermissionsAPI.revokeAccess(permissionId);
      
      if (response.success) {
        // Refresh permissions after successful revoke
        await loadAccessibleValueChains();
        return { success: true };
      } else {
        return { success: false, error: response.error || 'Failed to revoke access' };
      }
    } catch (err: any) {
      console.error('Error revoking access:', err);
      return { success: false, error: 'Failed to revoke access' };
    }
  }, [isAuthenticated, user, loadAccessibleValueChains]);

  /**
   * Check if user has access to a specific value chain
   */
  const hasAccess = useCallback((valueChainId: string): boolean => {
    return accessibleValueChains.some(chain => 
      chain.element_id === valueChainId || chain.value_chain_id === valueChainId
    );
  }, [accessibleValueChains]);

  /**
   * Get permission level for a specific value chain
   */
  const getPermissionLevel = useCallback((valueChainId: string): string | null => {
    const chain = accessibleValueChains.find(chain => 
      chain.element_id === valueChainId || chain.value_chain_id === valueChainId
    );
    return chain ? chain.permission_level : null;
  }, [accessibleValueChains]);

  /**
   * Refresh permissions
   */
  const refreshPermissions = useCallback(async (): Promise<void> => {
    await loadAccessibleValueChains();
  }, [loadAccessibleValueChains]);

  /**
   * Check if user has any value chain access
   * @param level - Permission level (default: 'read')
   * @returns True if user has any value chain access
   */
  const hasAnyValueChainAccess = useCallback((level: string = 'read'): boolean => {
    if (!isAuthenticated || !user) return false;
    return accessibleValueChains.length > 0;
  }, [isAuthenticated, user, accessibleValueChains]);

  /**
   * Get accessible value chains for a specific permission level
   * @param level - Permission level (default: 'read')
   * @returns Array of accessible value chains
   */
  const getAccessibleValueChains = useCallback((level: string = 'read'): ValueChain[] => {
    return accessibleValueChains;
  }, [accessibleValueChains]);

  /**
   * Check if user can view a specific value chain
   * @param valueChainId - Value chain ID to check
   * @returns True if user can view the value chain
   */
  const canViewValueChain = useCallback((valueChainId: string): boolean => {
    return hasAccess(valueChainId);
  }, [hasAccess]);

  // Load accessible value chains when component mounts or dependencies change
  useEffect(() => {
    loadAccessibleValueChains();
  }, [loadAccessibleValueChains]);

  return {
    accessibleValueChains,
    allValueChains,
    loading,
    error,
    loadAccessibleValueChains,
    loadAllValueChains,
    grantAccess,
    revokeAccess,
    hasAccess,
    getPermissionLevel,
    refreshPermissions,
    hasAnyValueChainAccess,
    getAccessibleValueChains,
    canViewValueChain,
  };
};
